/* -------------------------- Design imports start -------------------------- */
import { Delete, Download, EditRounded, VisibilityRounded } from '@mui/icons-material'
import CheckIcon from '@mui/icons-material/Check'
import ErrorIcon from '@mui/icons-material/Error'
import ReplayIcon from '@mui/icons-material/Replay'
import {
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import LogTool from '../../logger/logTools'
import { formatBytes } from '../../utils/functions'
import { type FileInfo } from '../../utils/types'
import { type ReactElement } from 'react'
import { isSupportedImageFormat } from '../imageManipulation/utils/functions'

/* ------------------------- Functional imports end ------------------------- */

export interface InputFileListFileActions {
  onRemoveFileFromList: (file: FileInfo) => void
  enableDownloadFile?: boolean
  onReUploadFile?: (file: FileInfo, url: string) => Promise<void>
  onDeleteFile?: (file: FileInfo) => Promise<void>
  onViewFile?: (file: FileInfo) => void
  onEditFile?: (file: FileInfo) => void
}

export type InputFileListTableColumnIndices = 'name' | 'size' | 'lastModified' | 'actions'

interface Props extends InputFileListFileActions {
  disabled?: boolean
  files: FileInfo[]
  noFilesInfo: string
  columns?: InputFileListTableColumnIndices[]
  sx?: React.CSSProperties
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
/**
 * Render a list of files, showing their upload status and available actions.
 * @param props
 * @returns
 */
export default function InputFileList(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const {
    disabled = false,
    files,
    noFilesInfo,
    onRemoveFileFromList,
    enableDownloadFile = true,
    onReUploadFile,
    onDeleteFile,
    onViewFile,
    onEditFile,
    columns = ['name', 'size', 'lastModified', 'actions'],
    sx = {},
  } = props
  const log = new LogTool({ context: 'InputFileList', enable: true, logLevel: 'debug' })
  const { t } = useTranslation()
  /* --------------------------- Non state data end --------------------------- */

  /* ------------------------- Utility functions start ------------------------ */
  /**
   * Returns whether the provided file is known by the backend (was uploaded
   * before) or not.
   * @param file The file to check.
   * @returns True, if the file has been uploaded before, otherwhise false
   */
  function isUploadedFile(file: FileInfo): boolean {
    return (
      Object.hasOwn(file, 'self') &&
      Boolean(file.self) &&
      Object.hasOwn(file, 'document') &&
      Boolean(file.document)
    )
  }
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleDownloadFile = (file: FileInfo): void => {
    log.info('downloadFile', file)
    const link = document.createElement('a')
    if (!link) throw new Error('Failed to create link element')
    if (!file.document)
      throw new Error("Failed to download file. The file doesn't have a document url.")
    link.href = file.document
    link.setAttribute('download', file.name ?? '')
    document.body.appendChild(link)
    link.click()
    link.remove()
  }
  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const renderUploadStatusIcon = (file: FileInfo): React.ReactNode | undefined | null => {
    if (!file.uploadStatus) return null

    if (file.uploadStatus === 'loading') {
      return <CircularProgress size="1rem" />
    }
    if (file.uploadStatus === 'success') {
      return <CheckIcon color="success" fontSize="small" />
    }
    if (file.uploadStatus === 'uploadError') {
      return (
        <Tooltip arrow title={t('common:feedback.error.uploadSingleFile')}>
          <ErrorIcon color="error" fontSize="small" />
        </Tooltip>
      )
    }
  }

  const renderFileActions = (file: FileInfo): React.ReactNode => (
    <Stack direction="row" spacing={1}>
      {!isUploadedFile(file) && file.uploadStatus === 'uploadError' && onReUploadFile && (
        <IconButton
          disabled={disabled}
          onClick={() => onReUploadFile(file, file.uploadUrl as string)}
        >
          <ReplayIcon />
        </IconButton>
      )}
      {!isUploadedFile(file) && (
        <IconButton
          disabled={disabled || file.uploadStatus === 'loading'}
          id="removeFileFromListButton"
          onClick={() => onRemoveFileFromList(file)}
        >
          <Delete />
        </IconButton>
      )}
      {onDeleteFile && isUploadedFile(file) && (
        <IconButton disabled={disabled} id="deleteButton" onClick={() => onDeleteFile(file)}>
          <Delete />
        </IconButton>
      )}
      {enableDownloadFile && (
        <IconButton
          disabled={disabled}
          id="downloadButton"
          onClick={() => handleDownloadFile(file)}
        >
          <Download />
        </IconButton>
      )}
      {onViewFile && (
        <IconButton disabled={disabled} id="viewFileButton" onClick={() => onViewFile(file)}>
          <VisibilityRounded />
        </IconButton>
      )}
      {onEditFile && isSupportedImageFormat(file) && (
        <IconButton disabled={disabled} id="editFileButton" onClick={() => onEditFile(file)}>
          <EditRounded />
        </IconButton>
      )}
    </Stack>
  )

  /* -------------------------- Render constants end -------------------------- */

  log.debug('onEditFile ->', onEditFile)

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */

  return (
    <div className="container" style={{ ...sx, margin: '5px' }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '1rem' }}></TableCell>
            {columns.includes('name') && <TableCell>{t('common:content.label.name')}</TableCell>}
            {columns.includes('size') && <TableCell>{t('common:content.label.size')}</TableCell>}
            {columns.includes('lastModified') && (
              <TableCell>{t('common:content.label.lastModified')}</TableCell>
            )}
            {columns.includes('actions') && (
              <TableCell>{t('common:content.label.action')}</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {files.length === 0 ? (
            // there are no files for the current article yet.
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ padding: 3 }}>
                <Typography variant="body2">{noFilesInfo}</Typography>
              </TableCell>
            </TableRow>
          ) : (
            // there are files uploaded for the current article -> display them
            files.map((file, index) => (
              <TableRow key={index}>
                <TableCell
                  align="center"
                  sx={{
                    pl: '16px',
                    pr: 0,
                    pb: '12px',
                    overflow: 'hidden',
                  }}
                >
                  {renderUploadStatusIcon(file)}
                </TableCell>
                {columns.includes('name') && (
                  <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {file.name}
                  </TableCell>
                )}
                {columns.includes('size') && (
                  <TableCell>{formatBytes(file.size as number)}</TableCell>
                )}
                {columns.includes('lastModified') && (
                  <TableCell>{moment(file.lastModified).format('DD.MM.YYYY')}</TableCell>
                )}
                {columns.includes('actions') && <TableCell>{renderFileActions(file)}</TableCell>}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}
